import { Component, OnInit, forwardRef, Output, EventEmitter  } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { startWith, switchMap, debounceTime } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from 'src/app/oas-generator/app-config.service';
import { Application, Manifest } from '../../models/orion-plugin.model';
import { CalAngularService } from '@cvx/cal-angular';
import axios from 'axios';

@Component({
    selector: 'orion-application-lookup',
    templateUrl: './orion-application-lookup.component.html',
    styleUrls: ['./orion-application-lookup.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OrionApplicationLookupComponent),
            multi: true
        }
    ],
    standalone: false
})
export class OrionApplicationLookupComponent implements OnInit, ControlValueAccessor {
  @Output() busyStateChange = new EventEmitter<{ isBusy: boolean, message: string }>();
  
  setBusyState(isBusy: boolean, message: string = '') {
    this.busyStateChange.emit({isBusy, message});
  }

  searchControl = new FormControl();
  filteredOptions!: Observable<any[]>;
  manifest!: Manifest;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private http: HttpClient, private authService: CalAngularService) {}

  ngOnInit() {
    this.filteredOptions = this.searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(500),
      switchMap(value => this.search(value))
    );
  }
  
  private async searchApplications(filter: string): Promise<Application[]> {
    if (!filter.trim()) {
      return [];
    }
    this.setBusyState(true, "Searching for Orion Manifests");
    const uri: string = AppConfig.settings.apiUrl + "/plugins/orion/applications";
    const accessToken = await this.authorizeAsync();
    const response = await axios.get<Application[]>(uri, {
        params: {
            filter: filter
        },
        headers: {
            authorization: `Bearer ${accessToken}`
        },
    });
    this.setBusyState(false);
    return response.data;
}
private async getManifest(serviceId: string): Promise<Manifest> {
  this.setBusyState(true, "Loading Orion Manifest Data for Service Id: " + serviceId);
  const uri: string = AppConfig.settings.apiUrl + "/plugins/orion/manifests/"+serviceId;
  const accessToken = await this.authorizeAsync();
  const response = await axios.get<Manifest>(uri, {
      headers: {
          authorization: `Bearer ${accessToken}`
      },
  });
  this.setBusyState(false);
  return response.data;
}

private authorizeAsync(): Promise<string> {
    return new Promise((resolve, reject) => {
        try {
            const scopes = [`${AppConfig.settings.clientId}/.default`];
            const authResult: Observable<string> = this.authService.getAADToken(scopes, false) as Observable<string>;
            authResult.subscribe(value => {
                resolve(value);
            });
        } catch (err: any) {
            reject(err);
        }
    })
}

search(value: any): Observable<Application[]> {
  if (typeof value !== 'string' || !value.trim()) {
    return of([]);
  }
  return new Observable<Application[]>(observer => {
    this.searchApplications(value).then(data => {
      observer.next(data);
      observer.complete();
    }).catch(err => {
      observer.error(err);
    });
  });
}

  async onOptionSelected(event: any) {
    const selectedOption = event.option.value;
    this.searchControl.setValue(selectedOption.name, { emitEvent: false });
    this.manifest = await this.getManifest(selectedOption.serviceId);
    this.onChange(this.manifest);
  }
  
  writeValue(value: Manifest): void {
    if (value) {
      this.manifest = value;
      this.searchControl.setValue(value.application.name, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }
}